import {
  ontologyExecuteSqlQuery,
  ontologyGetTableDetails,
  ontologyListTables,
} from "../sdk";
import type { ExecuteQueryRequest } from "../sdk";

/**
 * Service class for table management operations
 * Provides higher-level abstractions over the generated SDK
 */
export class TableService {
  /**
   * Get all tables for an organization
   * @param orgId - Organization ID
   * @param namespace - Optional namespace filter
   * @returns List of tables
   */
  static async getTables(orgId: string, namespace?: string) {
    const response = await ontologyListTables({
      query: {
        org_id: orgId,
        namespace,
      },
    });
    return response.data || [];
  }

  /**
   * Get table details including schema and metadata
   * @param tableId - Table ID
   * @param orgId - Organization ID
   * @returns Table details
   */
  static async getTableDetails(tableId: string, orgId: string) {
    // Note: orgId is not needed for this endpoint as it uses RLS
    const response = await ontologyGetTableDetails({
      path: { table_id: tableId },
    });
    return response.data;
  }

  /**
   * Execute SQL query on lakehouse
   * @param query - SQL query to execute
   * @param orgId - Organization ID
   * @param limit - Optional row limit
   * @returns Query results
   */
  static async executeSQLQuery(query: string, orgId: string, limit?: number) {
    const request: ExecuteQueryRequest =
      {
        sql_query: query,
        org_id: orgId,
      };

    const response = await ontologyExecuteSqlQuery({
      body: request,
    });
    return response.data;
  }

  /**
   * Get table data preview
   * @param tableId - Table ID
   * @param orgId - Organization ID
   * @param limit - Optional row limit (default 100)
   * @returns Table data with schema and rows
   */
  static async getTablePreview(
    tableId: string,
    orgId: string,
    limit: number = 100
  ) {
    try {
      console.log("TableService: Getting table details for tableId:", tableId);
      const tableDetails = await this.getTableDetails(tableId, orgId);
      console.log("TableService: Table details:", tableDetails);

      const { namespace, table_name } = tableDetails;

      const query = `SELECT * FROM ${namespace}.${table_name} LIMIT ${limit}`;
      console.log("TableService: Executing query:", query);

      const result = await this.executeSQLQuery(query, orgId);
      console.log("TableService: SQL query result:", result);

      return result;
    } catch (error) {
      console.error("TableService: Failed to get table preview:", error);
      throw new Error(
        `Failed to get table preview: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }
}
